/** Pack controls stay scoped to one card, including repeated products and section reloads. */
export class XzCardPackPicker extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', this.update, { signal: this.controller.signal });
    // The carousel cancels mousedown to start dragging; keep native controls activatable.
    this.addEventListener('mousedown', (event) => event.stopPropagation(), { signal: this.controller.signal });
    this.addEventListener('click', (event) => event.stopPropagation(), { signal: this.controller.signal });
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  update = () => {
    const option = this.querySelector('select')?.selectedOptions[0];
    const id = option?.value || this.querySelector('[name="id"]')?.value ||
      this.querySelector('template[data-pack-variant]')?.dataset.packVariant;
    if (!id) return;
    this.dataset.selectedVariant = id;
    const card = this.closest('product-card, .resource-card__wrapper') || this;
    const available = option ? option.dataset.available === 'true' :
      !this.querySelector('.xz-card-pack-picker__availability')?.textContent.trim();
    const price = this.querySelector(`template[data-pack-variant="${id}"]`);
    if (price) this.querySelector('[data-pack-price]').replaceChildren(price.content.cloneNode(true));
    if (option) this.querySelector('.xz-card-pack-picker__summary').textContent = option.dataset.label;
    this.querySelector('.xz-card-pack-picker__availability').textContent = available ? '' : this.dataset.soldOutLabel;
    for (const input of this.querySelectorAll('[name="id"]')) input.value = id;
    for (const input of this.querySelectorAll('[name="quantity"]')) input.value = option?.dataset.quantityMin || '1';
    for (const button of this.querySelectorAll('[name="add"]')) {
      button.disabled = !available;
      // Keep the native component's animation markup while replacing only its label.
      for (const node of button.childNodes) if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
      let label = button.querySelector('[data-pack-add-label]');
      if (!label) { label = document.createElement('span'); label.dataset.packAddLabel = ''; button.prepend(label); }
      label.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }
    for (const link of card.querySelectorAll('a[href]')) {
      const url = new URL(link.href, document.baseURI);
      const product = new URL(this.dataset.productUrl, document.baseURI);
      if (url.origin !== product.origin || url.pathname !== product.pathname) continue;
      url.searchParams.set('variant', id);
      link.href = url.href;
    }
    if (option?.dataset.media) {
      const media = new URL(option.dataset.media, document.baseURI);
      card.dataset.featuredMediaUrl = media.href;
      const gallery = card.querySelector('slideshow-component');
      const slides = [...(gallery?.querySelectorAll('slideshow-slide') || [])];
      const selectedSlide = slides.find(slide => {
        const src = slide.querySelector('img')?.getAttribute('src');
        return src && new URL(src, document.baseURI).pathname === media.pathname;
      });
      if (selectedSlide) {
        // Use the same variant-image visibility contract as the native swatch picker.
        for (const slide of slides) {
          if (slide.hasAttribute('variant-image')) {
            slide.hidden = slide !== selectedSlide;
            slide.setAttribute('aria-hidden', String(slide.hidden));
          }
        }
        selectedSlide.hidden = false;
        gallery.select?.({ id: selectedSlide.getAttribute('slide-id') }, undefined, { animate: false });
        // Native selection can return early after the filtered slide list changes.
        selectedSlide.setAttribute('aria-hidden', 'false');
      } else if (card.matches('.resource-card__wrapper')) {
        for (const image of card.querySelectorAll('.resource-card__media img')) {
          image.removeAttribute('srcset');
          image.src = media.href;
        }
      }
    }
  };
}

if (!customElements.get('xz-card-pack-picker')) customElements.define('xz-card-pack-picker', XzCardPackPicker);
